import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SelectedDropdownItem } from '@galaxy/inventory-ui';
import { MarketplaceAppService } from '@galaxy/marketplace-apps/v1';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  FieldMask,
  ItemType,
  ItemsApiService,
  ListActivatableItemsFilters,
  ListActivatableItemsRequest,
  OrderFormFilter,
} from '@vendasta/marketplace-apps';
import { Addon, MarketplaceApp } from '@vendasta/marketplace-apps/v1';
import { BehaviorSubject, Observable, combineLatest, of, startWith } from 'rxjs';
import { map, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { AppEditionService } from '../../activation/core/app-edition.service';
import { Edition } from '../../activation/core/interface';
import { BillingApiService } from '../../core/billing/billing-api.service';
import { PurchaseCost } from '../../core/billing/purchase-cost';
import { HidePricingGuard } from '../../core/guards/hide-pricing-guard.service';
import { ActionListDialogComponent, ActionListDialogData } from './action-list-dialog.component';
import { ActionsApiService } from './actions.api.service';

export enum SelectionType {
  PRODUCT = 'product',
  ADDON = 'addon',
}

export interface ActivateItemDialogData extends ActionListDialogData {
  selectionType: SelectionType;
}

interface State {
  partnerId: string;
  selectedProduct: SelectedDropdownItem;
  selectedProductData: MarketplaceApp;
  selectedProductHasEditions: boolean;
  selectedProductEditions: Edition[];
  selectedProductHasAddons: boolean;
  selectedEdition: Edition;
  selectedProductDisplayName: string;
  selectedAddon: SelectedDropdownItem;
  selectedAddonData: Addon;
  numActiveAccounts: number;
  numAccountsToActivate: number;
  purchaseCost: PurchaseCost;
  hidePricing: boolean;
  addonFilters: ListActivatableItemsFilters;
  isButtonEnabled: boolean;
}

@Component({
  templateUrl: './activate-item-dialog.component.html',
  styleUrls: ['./action-list-dialog.component.scss', './action-list-actions.component.scss'],
})
export class ActivateItemDialogComponent extends ActionListDialogComponent implements OnInit {
  state$: Observable<State>;

  selectedProduct$$ = new BehaviorSubject<SelectedDropdownItem>(null);
  selectedEdition$$ = new BehaviorSubject<Edition>(null);
  selectedAddon$$ = new BehaviorSubject<SelectedDropdownItem>(null);

  selectedProductData$: Observable<MarketplaceApp>;
  selectedProductEditions$: Observable<Edition[]>;
  selectedProductHasAddons$: Observable<boolean>;
  selectedAddonData$: Observable<Addon>;
  previewLoading = false;
  buttonLoading = false;
  numActiveAccounts$: Observable<number>;
  numAccountsToActivate$: Observable<number>;
  purchaseCost$: Observable<PurchaseCost>;
  hidePricing$: Observable<boolean>;

  productFilters = new ListActivatableItemsFilters({
    itemTypes: [ItemType.ITEM_TYPE_APP],
    hasOrderForm: OrderFormFilter.ORDER_FORM_FILTER_NO_ORDER_FORM,
  });
  selectionType: SelectionType;
  SelectionType = SelectionType;
  useBulkActionsMicroservice: boolean;

  constructor(
    @Inject('PARTNER_ID') public partnerId$: Observable<string>,
    @Inject(MAT_DIALOG_DATA) private readonly data: ActivateItemDialogData,
    public dialogRef: MatDialogRef<ActivateItemDialogComponent>,
    public router: Router,
    private marketplaceAppsService: MarketplaceAppService,
    private editionsService: AppEditionService,
    private itemsApiService: ItemsApiService,
    private actionsService: ActionsApiService,
    private billingService: BillingApiService,
    private hidePricingGuard: HidePricingGuard,
    private snackbarService: SnackbarService,
  ) {
    super(dialogRef, router);
  }

  ngOnInit(): void {
    this.selectionType = this.data.selectionType;
    this.useBulkActionsMicroservice = this.data.useBulkActionsMicroservice;

    this.selectedProductData$ = this.selectedProduct$$.pipe(
      switchMap((selectedProduct) => {
        if (!selectedProduct) return of(null);
        return this.marketplaceAppsService.getApp(
          selectedProduct.appId,
          new FieldMask({ paths: ['appId', 'usesEditions', 'billingId'] }),
        );
      }),
      shareReplay(1),
    );
    this.selectedProductEditions$ = this.selectedProduct$$.pipe(
      switchMap((selectedProduct) => {
        if (!selectedProduct) return of(null);
        return this.editionsService.getEditionsForApp(selectedProduct.appId);
      }),
      shareReplay(1),
    );
    this.selectedProductHasAddons$ = combineLatest([this.partnerId$, this.selectedProduct$$]).pipe(
      switchMap(([partnerId, selectedProduct]) => {
        if (!selectedProduct) return of(null);
        return this.itemsApiService.listActivatableItems(
          new ListActivatableItemsRequest({
            sellerId: partnerId,
            cursor: '',
            pageSize: 1,
            filters: new ListActivatableItemsFilters({
              itemTypes: [ItemType.ITEM_TYPE_ADDON],
              requiredItemId: selectedProduct.appId,
              hasOrderForm: OrderFormFilter.ORDER_FORM_FILTER_NO_ORDER_FORM,
            }),
          }),
        );
      }),
      map((resp) => (resp ? Boolean(resp.activatableItemSummaries?.length) : null)),
      shareReplay(1),
    );
    this.selectedAddonData$ = this.selectedAddon$$.pipe(
      switchMap((selectedAddon) => {
        if (!selectedAddon) return of(null);
        return this.marketplaceAppsService.getAddon(selectedAddon.appId, selectedAddon.addonId);
      }),
      shareReplay(1),
    );
    // TODO: conditionally get count from new api
    this.numActiveAccounts$ = this.selectedProduct$$.pipe(
      switchMap((selectedProduct) => {
        if (!selectedProduct) return of(0);
        return this.actionsService.getCountActiveProductsForList(this.actionList.id, selectedProduct.appId);
      }),
      shareReplay(1),
    );
    this.numAccountsToActivate$ = this.numActiveAccounts$.pipe(
      map((activeAccounts) => {
        if (this.selectionType === SelectionType.PRODUCT) return this.actionList.accounts - activeAccounts;
        return activeAccounts;
      }),
    );
    this.hidePricing$ = this.hidePricingGuard.canActivate().pipe(shareReplay(1), startWith(true));
    let billingId$: Observable<string>;
    if (this.selectionType === SelectionType.PRODUCT) {
      billingId$ = combineLatest([this.selectedProductData$, this.selectedEdition$$]).pipe(
        map(([selectedProduct, selectedEdition]) => {
          if (!selectedProduct || (selectedProduct?.usesEditions && !selectedEdition)) return null;
          return !selectedProduct.usesEditions ? selectedProduct.billingId : selectedEdition.billingId;
        }),
      );
    } else {
      billingId$ = this.selectedAddonData$.pipe(map((addon) => addon?.billingId));
    }
    this.purchaseCost$ = combineLatest([
      this.partnerId$,
      billingId$,
      this.numAccountsToActivate$,
      this.hidePricing$,
    ]).pipe(
      switchMap(([partnerId, billingId, accountsToActivate, hidePricing]) => {
        if (!billingId || !accountsToActivate || hidePricing) {
          return of(null);
        }
        return this.billingService.getPurchaseCost(partnerId, billingId, accountsToActivate);
      }),
      tap(() => {
        this.previewLoading = false;
      }),
      shareReplay(1),
    );

    this.state$ = combineLatest([
      this.partnerId$,
      this.selectedProduct$$,
      this.selectedProductData$,
      this.selectedProductEditions$,
      this.selectedProductHasAddons$,
      this.selectedEdition$$,
      this.selectedAddon$$,
      this.selectedAddonData$,
      this.numActiveAccounts$,
      this.numAccountsToActivate$,
      this.purchaseCost$,
      this.hidePricing$,
    ]).pipe(
      map(
        ([
          partnerId,
          selectedProduct,
          selectedProductData,
          selectedProductEditions,
          selectedProductHasAddons,
          selectedEdition,
          selectedAddon,
          selectedAddonData,
          numActiveAccounts,
          numAccountsToActivate,
          purchaseCost,
          hidePricing,
        ]) => {
          const state = {
            partnerId: partnerId,
            selectedProduct: selectedProduct,
            selectedProductData: selectedProductData,
            selectedProductHasEditions:
              selectedProductData !== null && selectedProductEditions !== null
                ? !!selectedProductData.usesEditions && selectedProductEditions.length > 0
                : null,
            selectedProductEditions: selectedProductEditions,
            selectedProductHasAddons: selectedProductHasAddons,
            selectedEdition: selectedEdition,
            selectedProductDisplayName: this.selectedProductDisplayName(selectedProduct, selectedEdition),
            selectedAddon: selectedAddon,
            selectedAddonData: selectedAddonData,
            numActiveAccounts: numActiveAccounts,
            numAccountsToActivate: numAccountsToActivate,
            purchaseCost: purchaseCost,
            hidePricing: hidePricing,
            addonFilters: new ListActivatableItemsFilters({
              itemTypes: [ItemType.ITEM_TYPE_ADDON],
              requiredItemId: selectedProduct?.appId,
              hasOrderForm: OrderFormFilter.ORDER_FORM_FILTER_NO_ORDER_FORM,
            }),
            isButtonEnabled: false,
          };
          state.isButtonEnabled = this.isButtonEnabled(this.selectionType, state);
          return state;
        },
      ),
    );
  }

  selectProduct(product: SelectedDropdownItem): void {
    this.previewLoading = true;

    // TODO: this is a hack because the item filter doesn't react
    // to config changes, so hide the add-on filter to reload the
    // add-ons when the selected product changes.
    this.selectedProduct$$.next(null);

    this.selectedProduct$$.next(product);
    this.selectedEdition$$.next(null);
    this.selectedAddon$$.next(null);
  }

  selectEdition(edition: Edition): void {
    this.previewLoading = true;
    this.selectedEdition$$.next(edition);
  }

  selectAddon(addon: SelectedDropdownItem): void {
    this.previewLoading = true;
    this.selectedAddon$$.next(addon);
  }

  activateItem(): void {
    if (this.buttonLoading) {
      return;
    }
    this.buttonLoading = true;

    if (this.selectionType === SelectionType.PRODUCT) {
      this.activateProduct();
    } else {
      this.activateAddon();
    }
  }

  private activateProduct(): void {
    let response$: Observable<any>;
    const input$ = combineLatest([this.selectedProduct$$, this.selectedEdition$$, this.numAccountsToActivate$]);
    if (this.useBulkActionsMicroservice) {
      response$ = input$.pipe(
        switchMap(([selectedProduct, selectedEdition, accountsToActivate]) => {
          return this.actionsService.activateProductBulkAction(
            this.actionList.id,
            selectedProduct.appId,
            selectedProduct.name,
            accountsToActivate,
            selectedEdition?.editionId ?? '',
            selectedEdition?.editionName ?? '',
          );
        }),
        shareReplay(1),
      );
    } else {
      response$ = input$.pipe(
        switchMap(([selectedProduct, selectedEdition, accountsToActivate]) => {
          return this.actionsService.activateProductForList(
            this.actionList.id,
            selectedProduct.appId,
            selectedProduct.name,
            accountsToActivate,
            selectedEdition?.editionId ?? '',
            selectedEdition?.editionName ?? '',
          );
        }),
        shareReplay(1),
      );
    }
    response$.pipe(withLatestFrom(this.selectedProduct$$, this.selectedEdition$$), take(1)).subscribe({
      next: ([, selectedProduct, selectedEdition]) => {
        this.snackbarService.openSuccessSnack(
          `Activating ${this.selectedProductDisplayName(selectedProduct, selectedEdition)} for ${
            this.actionList.name
          }.`,
        );
        this.dialogRef.close(true);
        this.buttonLoading = false;
      },
      error: (err) => {
        if (err.code === 412) {
          this.snackbarService.openErrorSnack(
            'This list is too long. To activate this product, email support@vendasta.com.',
          );
        } else {
          this.snackbarService.openErrorSnack(
            `Failed to activate product for ${this.actionList.name}. Please try again.`,
          );
        }
        this.buttonLoading = false;
      },
    });
  }

  private activateAddon(): void {
    let response$: Observable<any>;
    const input$ = combineLatest([this.selectedProduct$$, this.selectedAddon$$, this.numAccountsToActivate$]);
    if (this.useBulkActionsMicroservice) {
      response$ = input$.pipe(
        switchMap(([selectedProduct, selectedAddon, accountsToActivate]) => {
          return this.actionsService.activateAddonBulkAction(
            this.actionList.id,
            selectedProduct.appId,
            selectedProduct.name,
            accountsToActivate,
            selectedAddon.addonId,
            selectedAddon.name,
          );
        }),
        shareReplay(1),
      );
    } else {
      response$ = input$.pipe(
        switchMap(([selectedProduct, selectedAddon, accountsToActivate]) => {
          return this.actionsService.activateAddonForList(
            this.actionList.id,
            selectedProduct.appId,
            selectedProduct.name,
            selectedAddon.addonId,
            selectedAddon.name,
            accountsToActivate,
          );
        }),
        shareReplay(1),
      );
    }
    response$.pipe(withLatestFrom(this.selectedAddon$$), take(1)).subscribe({
      next: ([, selectedAddon]) => {
        this.snackbarService.openSuccessSnack(`Activating ${selectedAddon.name} for ${this.actionList.name}.`);
        this.dialogRef.close(true);
        this.buttonLoading = false;
      },
      error: (err) => {
        if (err.code === 412) {
          this.snackbarService.openErrorSnack(
            'This list is too long. To activate this add-on, email support@vendasta.com.',
          );
        } else {
          this.snackbarService.openErrorSnack(
            `Failed to activate add-on for ${this.actionList.name}. Please try again.`,
          );
        }
        this.buttonLoading = false;
      },
    });
  }

  private selectedProductDisplayName(selectedProduct: SelectedDropdownItem, selectedEdition: Edition): string {
    if (!selectedEdition?.editionName) {
      return selectedProduct?.name;
    }
    return `${selectedProduct?.name} ${selectedEdition?.editionName}`;
  }

  getFrequencyNoun(freq: string): string {
    let returnValue = '';
    if (freq) {
      switch (freq.toLowerCase()) {
        case 'daily':
          returnValue = 'day';
          break;
        case 'weekly':
          returnValue = 'week';
          break;
        case 'monthly':
          returnValue = 'month';
          break;
        case 'yearly':
          returnValue = 'year';
          break;
        case 'once':
        case 'one time':
          returnValue = 'one time';
          break;
        case 'other':
          returnValue = 'other';
          break;
      }
    }
    return returnValue;
  }

  private isButtonEnabled(selectionType: SelectionType, state: State): boolean {
    if (selectionType === SelectionType.PRODUCT) {
      return (
        !!state.selectedProduct &&
        (state.selectedProductHasEditions === false || !!state.selectedEdition) &&
        state.numAccountsToActivate > 0
      );
    }
    return !!state.selectedProduct && !!state.selectedAddon && state.numAccountsToActivate > 0;
  }
}
